// @flow
import React, { useContext, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { isEmpty, get } from 'lodash';
import { Formik, Form } from 'formik';

import ReactAppContext from 'Context/ReactAppContext';
import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { t } from 'Src/i18n';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { ID_MODAL_HEADER__PLE__NEWSLETTER } from 'Components/utils/identifiers/constants';
import { ICONS } from 'Components/utils/icon/icons';
import { COLORS } from 'Components/utils/color/colors';
import { onCheckboxChange } from 'Components/widgets/profile-info/newsletter-subscription/NewsletterSubscription.utils';
import { formatedData, submitSubscriptionForm } from 'Views/newsletter/newsletterActions';
import ratios from 'Components/utils/ratios/ratios.utils.js';

import { Button } from 'Components/frameworks/button/Button.jsx';
import { FormErrors } from 'Components/frameworks/form-errors/FormErrors.jsx';
import { FormikInput } from 'Components/frameworks/formik/FormikInput.jsx';
import Image from 'Components/frameworks/image/Image.jsx';
import { Modal } from 'Components/frameworks/modal/Modal.jsx';
import { Text } from 'Components/frameworks/text/Text.jsx';
import { Title } from 'Components/frameworks/title/Title.jsx';

import {
  NewsletterForm,
  subscriptionSchema,
} from 'Components/widgets/newsletter-form/NewsletterForm.jsx';

export const POPIN_NEWSLETTER = 'POPIN_NEWSLETTER';

type Props = {
  checkboxes: Array<{
    id: string,
    description: string,
  }>,
  legalInformations: Array<{
    id: number,
    description: string,
  }>,
  onClose: () => void,
};

export const NEWSLETTER_SUBSCRIPTION_VALIDATION = 'NEWSLETTER_SUBSCRIPTION_VALIDATION';

export function PopinNewsletter({ checkboxes = [], legalInformations = [], onClose }: Props) {
  const appContext = useContext(ReactAppContext);

  const defaultPayload = {
    categoryGA: 'newsletter',
    actionGA: 'shopping',
  };
  const track = useTracking(defaultPayload);
  const [submitErrors, setSubmitErrors] = useState([]);
  const dispatch = useDispatch();
  const { isTablet } = useContext(ResponsiveContext);
  const labelConfirmation = `${t('newsletter.confirmation.headline')} \n ${t(
    'newsletter.confirmation.description',
  )}`;

  useEffect(
    () =>
      track({
        labelGA: 'displayPopin',
      }),
    [],
  );

  return (
    <Modal
      ariaLabel={t('newsletter.popin.title')}
      ariaLabelledby={ID_MODAL_HEADER__PLE__NEWSLETTER}
      backdropColor="#000"
      closeLabel={t('common.close')}
      onClose={onClose}
      size="large"
    >
      <Image
        className="w-full sm:w-12/25 md:w-1/3 h-full flex-shrink-0 sm:absolute"
        imageUrl={t('newsletter.popin.imageUrl')}
        mandatoryWidthAndHeight={isTablet ? { w: 320, h: 700 } : { w: 500, h: 200 }}
        ratioKey={ratios.XS}
      />
      <div className="PopinNewsletter-content m-6">
        <Title
          iconName={ICONS.NEWSLETTER}
          id={ID_MODAL_HEADER__PLE__NEWSLETTER}
          theme="title-brand-2"
          iconSize="1.5rem"
          iconColor={COLORS.BLUE}
        >
          {t('newsletter.popin.title')}
        </Title>
        <Text className="mt-2 mb-3" text={t('newsletter.popin.subtitle')} />
        <Formik
          validationSchema={subscriptionSchema}
          initialValues={{
            ...checkboxes.reduce((newslettersCheckbox, newsletter) => {
              newslettersCheckbox[newsletter.id] = false;
              return newslettersCheckbox;
            }, {}),
          }}
          onSubmit={async values => {
            const response = await submitSubscriptionForm({
              appContext,
              data: formatedData(values),
              isSubscription: true,
            });

            track({
              labelGA: 'registrationPopin',
              user: {
                neolaneID: get(response, 'subscription.neolaneId'),
              },
            });

            const error = get(response, 'subscription.error.errorDescription', '');
            if (!isEmpty(error)) return setSubmitErrors([error]);

            dispatch({
              type: NEWSLETTER_SUBSCRIPTION_VALIDATION,
              payload: { toastLabel: labelConfirmation },
            });
            onClose && onClose();
          }}
        >
          {({ setFieldValue, isValid }) => (
            <Form>
              <NewsletterForm />
              {checkboxes.map(checkbox => (
                <div className="mt-4" key={checkbox.id}>
                  <FormikInput
                    name={checkbox.id}
                    type="checkbox"
                    label={checkbox.description}
                    value={checkbox.id}
                    onChange={e => onCheckboxChange(e.target, setFieldValue)}
                  />
                </div>
              ))}
              <div className="mt-4 text-right">
                {!isEmpty(submitErrors) && <FormErrors texts={submitErrors} />}
                <Button
                  type="submit"
                  colorBg={COLORS.BLUE}
                  colorBorder={COLORS.BLUE}
                  isDisabled={!isValid}
                >
                  {t('newsletter.popin.cta')}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
        <div className="mt-4">
          {legalInformations.map(info => {
            return !isEmpty(info.description) ? (
              <div key={`LegalInfo-${info.id}`}>
                <Text theme="text-sans-2" text={info.description} />
              </div>
            ) : null;
          })}
        </div>
      </div>
    </Modal>
  );
}
